// Generic CSV loader — best-effort parser for unknown banks / portals.
// Port of scripts/loaders.py::load_generic_csv (~150 lines) and its helpers.
//
// The strategy: sniff the delimiter, skip preamble until a date-y header
// is found, then score each column for "looks like a date" / "looks like
// an amount". The best date column + the best amount column (or a
// debit/credit pair when those exact headers are present) define the row
// shape. Everything else becomes part of the description.
//
// This is what catches anything that ISN'T already a known portal — TD,
// Simplii, RBC card exports, a credit union's quirky format, a fintech
// no one's heard of. It won't always get the sign right (some banks ship
// expenses as positive instead of negative), but the user will see
// obviously-wrong-signs in the dashboard and can spot-fix them.

import {
  type Transaction,
  type AccountKind,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
} from "../common.js";
import { parseCsv, toDictRows } from "../csv.js";

// ─────────────────────────────────────────────────────────────────────
// Header-token tables. Mirror scripts/loaders.py constants of the same
// name. Case-insensitive substring matches on column header text.
// ─────────────────────────────────────────────────────────────────────
const DATE_HEADER_TOKENS = [
  "date", "posted", "transaction", "trans",
  "effective", "completed", "when", "settled",
  // "created" / "created at" — Shopify, Stripe, many SaaS exports.
  "created",
];
const DESC_HEADER_TOKENS = [
  "description", "memo", "narrative", "details",
  "merchant", "vendor", "payee", "name",
  "particulars", "reference", "type",
];
const DEBIT_HEADER_TOKENS = [
  "debit", "withdraw", "withdrawal", "out", "charge",
  "payment", "spend", "paid out", "money out",
];
const CREDIT_HEADER_TOKENS = [
  "credit", "deposit", "in", "received", "paid in",
  "money in", "inflow",
];
const STATUS_HEADER_TOKENS = ["status", "state"];
const DROP_STATUS_VALUES = new Set([
  "pending", "cancelled", "canceled", "failed", "declined",
  "void", "voided", "reversed", "rejected", "incomplete",
]);
const ACCOUNT_HEADER_TOKENS = [
  "account number", "account #", "account no",
  "card number", "card #", "account",
];

// Markers that strongly suggest this CSV is a credit-card export rather
// than a deposit account. Credit-card exports tend to ship `positive = charge`
// natively (AmEx-style), which already matches our project convention —
// so for these files we SKIP the bank-convention sign flip. Without this
// detection, AmEx-clone files come out with every charge as income.
const CREDIT_CARD_HEADER_MARKERS = [
  "card number", "card no", "card member", "cardholder",
  "statement balance", "minimum payment", "new balance",
  "credit limit", "available credit", "previous balance",
];

// Deposit / chequing-account header markers. The "Balance" token has to
// stand alone (or be paired with "Available") — "Statement Balance" /
// "New Balance" are credit-card-side and live in the list above.
const DEPOSIT_HEADER_MARKERS = [
  "running balance", "ledger balance", "available balance",
  "withdrawal", "deposit", "atm", "bill pay",
  "account type", // RBC's metadata column
];

// Transaction-description patterns that ONLY appear on deposit accounts
// — credit cards don't get direct payroll deposits, interest credits, or
// e-transfers in. Strong "debit account" signal when seen.
const DEPOSIT_TX_PATTERNS: RegExp[] = [
  /\bpayroll\s+deposit\b/i,
  /\bdirect\s+deposit\b/i,
  /\binterest\s+credit\b/i,
  /\binterest\s+earned\b/i,
  /\be[\-\s]?transfer\b/i,
  /\bcheque\b/i,
  /\bbill\s+pay\b/i,
  /\bach\s+(deposit|credit|debit)\b/i,
  /\batm\s+withdrawal\b/i,
];

// ─────────────────────────────────────────────────────────────────────
// Multi-signal sign detection
// ─────────────────────────────────────────────────────────────────────
//
// Replaces the old single-signal looksLikeCreditCardExport boolean.
// Each signal returns a score in [-1, +1]:
//   -1.0 = strongly liability (credit card)
//   +1.0 = strongly debit (deposit / chequing account)
//    0.0 = no opinion
// Plus a weight that reflects confidence in the signal source. The
// weighted average is the final score; |score| < SIGN_THRESHOLD →
// default to debit (the commonest case) so a near-zero ambiguous file
// doesn't get pushed in either direction by noise.
//
// Surfaced on every Transaction via raw.sign_decision for debugging
// when a file's signs come out backwards.

export interface SignSignal {
  /** Source of this signal (for debug output). */
  source: string;
  /** [-1, +1]. -1 = strongly liability, +1 = strongly debit. */
  score: number;
  /** Confidence multiplier — higher means this signal carries more weight. */
  weight: number;
  /** Human-readable reason this signal scored as it did. */
  reason: string;
}

export interface SignDecision {
  account_kind: "debit" | "liability";
  /** If true, the bank ships negative=expense and we flip. Otherwise
   *  the bank already ships positive=expense (matches our convention). */
  flip_negatives: boolean;
  /** Weighted average of signals: [-1, +1]. */
  total_score: number;
  signals: SignSignal[];
}

const SIGN_THRESHOLD = 0.15;

// Score the file via every available signal. Caller picks the action
// from `decision.flip_negatives` (apply to single signed-amount column)
// and `decision.account_kind` (lands on each Transaction).
export function decideSignFromSignals(args: {
  headers: string[];
  rows: Array<Record<string, string>>;
  amountCol?: string;
  hasDebitCreditSplit: boolean;
  institutionKind: string;
  filenameLower: string;
}): SignDecision {
  const { headers, rows, amountCol, hasDebitCreditSplit, institutionKind, filenameLower } = args;
  const signals: SignSignal[] = [];

  // 1. Header markers — explicit credit-card vs deposit-account columns.
  const headersLower = headers.join(" ").toLowerCase();
  const ccHits = CREDIT_CARD_HEADER_MARKERS.filter((m) => headersLower.includes(m));
  const depositHits = DEPOSIT_HEADER_MARKERS.filter((m) => headersLower.includes(m));
  if (ccHits.length > 0) {
    signals.push({
      source: "header_markers",
      score: -1,
      weight: 2,
      reason: `Credit-card header markers found: ${ccHits.slice(0, 3).join(", ")}`,
    });
  } else if (depositHits.length > 0) {
    signals.push({
      source: "header_markers",
      score: +1,
      weight: 2,
      reason: `Deposit-account header markers found: ${depositHits.slice(0, 3).join(", ")}`,
    });
  }

  // 2. Institution-detected kind from the bank-token list.
  if (institutionKind === "credit" || institutionKind === "mastercard" || institutionKind === "visa") {
    signals.push({
      source: "institution_kind",
      score: -1,
      weight: 2,
      reason: `Institution detected as ${institutionKind}`,
    });
  } else if (institutionKind === "chequing" || institutionKind === "checking" || institutionKind === "savings") {
    signals.push({
      source: "institution_kind",
      score: +1,
      weight: 2,
      reason: `Institution detected as ${institutionKind}`,
    });
  }

  // 3. Filename hint.
  if (/mastercard|visa|credit[\-_\s]?card|\bcc\b/.test(filenameLower)) {
    signals.push({
      source: "filename_hint",
      score: -0.8,
      weight: 1,
      reason: "Filename matches a credit-card keyword",
    });
  } else if (/chequing|checking|savings|deposit/.test(filenameLower)) {
    signals.push({
      source: "filename_hint",
      score: +0.8,
      weight: 1,
      reason: "Filename matches a deposit-account keyword",
    });
  }

  // 4. Transaction-pattern scan — count rows whose description matches
  // a deposit-account-only phrase. Only fires if we have enough rows.
  if (rows.length >= 3) {
    let hits = 0;
    const sample = rows.slice(0, 50);
    for (const row of sample) {
      // Concatenate every text-y cell so we catch the pattern wherever
      // the description happens to live.
      const text = Object.values(row).join(" ");
      if (DEPOSIT_TX_PATTERNS.some((p) => p.test(text))) hits++;
    }
    if (hits >= 1) {
      // Even one PAYROLL DEPOSIT is a strong "this is a chequing account" signal.
      signals.push({
        source: "tx_patterns",
        score: +1,
        weight: 1.5,
        reason: `${hits} row(s) match deposit-only patterns (e.g. payroll/direct deposit)`,
      });
    }
  }

  // 5. Sign-distribution pre-scan. Only applies when there's a single
  // signed-amount column. Strong-positive distribution leans liability;
  // mixed leans debit.
  if (!hasDebitCreditSplit && amountCol) {
    let nPos = 0;
    let nNeg = 0;
    for (const row of rows) {
      const raw = (row[amountCol] || "").trim();
      if (!raw) continue;
      const v = parseAmount(raw);
      if (v > 0) nPos++;
      else if (v < 0) nNeg++;
    }
    const total = nPos + nNeg;
    if (total >= 3) {
      const posShare = nPos / total;
      if (posShare >= 0.9) {
        signals.push({
          source: "sign_distribution",
          score: -0.5,
          weight: 1,
          reason: `${nPos}/${total} rows are positive (>=90%) — credit-card style`,
        });
      } else if (nNeg > 0 && nPos > 0 && posShare >= 0.2 && posShare <= 0.8) {
        signals.push({
          source: "sign_distribution",
          score: +0.5,
          weight: 1,
          reason: `Mixed-sign distribution (${nPos} pos / ${nNeg} neg) — deposit-account style`,
        });
      }
    }
  }

  const totalWeight = signals.reduce((s, x) => s + x.weight, 0);
  const totalScore = totalWeight > 0
    ? signals.reduce((s, x) => s + x.score * x.weight, 0) / totalWeight
    : 0;

  const accountKind: "debit" | "liability" =
    totalScore < -SIGN_THRESHOLD ? "liability" : "debit";
  // Liability accounts (credit cards) ship positive=charge already;
  // debit accounts ship negative=withdrawal and we flip.
  const flipNegatives = accountKind === "debit";

  return { account_kind: accountKind, flip_negatives: flipNegatives, total_score: totalScore, signals };
}

// Lines that LOOK like data (have a date + amount) but aren't real
// transactions — opening balance, total, etc. Same patterns as the
// Python NON_TX_PATTERNS list.
const NON_TX_PATTERNS: RegExp[] = [
  /^\s*opening balance\b/i,
  /^\s*closing balance\b/i,
  /^\s*beginning balance\b/i,
  /^\s*ending balance\b/i,
  /^\s*previous balance\b/i,
  /^\s*new balance\b/i,
  /^\s*current balance\b/i,
  /^\s*balance forward\b/i,
  /^\s*balance brought forward\b/i,
  /^\s*balance carried forward\b/i,
  /^\s*carried forward\b/i,
  /^\s*brought forward\b/i,
  /^\s*sub[\s-]?total\b/i,
  /^\s*statement total\b/i,
  /^\s*total(?:\s+(?:debits?|credits?|charges?|payments?|deposits?|expenses?|fees?))?\s*$/i,
  /^\s*total\s+(?:debits?|credits?|charges?|payments?|deposits?|expenses?|fees?)\b/i,
  /^\s*minimum payment due\b/i,
  /^\s*amount due\b/i,
];

function isNonTxRow(description: string): boolean {
  if (!description) return false;
  return NON_TX_PATTERNS.some((p) => p.test(description));
}

// ─────────────────────────────────────────────────────────────────────
// Cheap "looks like X" predicates used to score columns
// ─────────────────────────────────────────────────────────────────────

// Mirrors _looks_like_date. Accepts an optional ISO time component
// (Venmo's "2026-04-01T14:22:00" style); the loader chops the time off
// before parseDate.
const DATE_RE = new RegExp(
  "^\\s*(?:" +
    // ISO date or ISO date+time. Time portion accepts optional whitespace
    // before the timezone offset (Shopify writes "2026-03-01 13:08:00 -0500").
    "\\d{4}[-/]\\d{1,2}[-/]\\d{1,2}(?:[T ]\\d{1,2}:\\d{2}(?::\\d{2})?(?:\\s*[+-]\\d{2}:?\\d{2}|Z)?)?" +
    "|\\d{1,2}[-/]\\d{1,2}[-/]\\d{2,4}" +
    "|\\d{8}" +
    "|\\d{1,2}\\s+[A-Za-z]{3,}\\s+\\d{2,4}" +
    "|[A-Za-z]{3,}\\.?\\s+\\d{1,2},?\\s+\\d{2,4}" +
  ")\\s*$",
);

function looksLikeDate(s: string): boolean {
  const trimmed = (s || "").trim();
  if (!trimmed) return false;
  return DATE_RE.test(trimmed);
}

// Mirrors _looks_like_amount. Blanks score as OK so debit/credit columns
// (mostly blank by design) get good scores.
function looksLikeAmount(s: string): boolean {
  let t = (s || "").trim();
  if (!t) return true;
  t = t.toUpperCase();
  t = t.replace(/\b(USD|CAD|EUR|GBP|JPY|AUD|CHF|HKD|SGD|MXN|BRL|ZAR|CR)\b/g, "");
  t = t.replace(/[$€£¥]/g, "");
  t = t.replace(/[()]/g, "").trim();
  if (t.endsWith("-")) t = t.slice(0, -1).trim();
  if (t.startsWith("-") || t.startsWith("+")) t = t.slice(1).trim();
  return /^[\d,. ]+$/.test(t) && /\d/.test(t);
}

function headerMatchesAny(header: string, tokens: readonly string[]): boolean {
  const h = (header || "").toLowerCase();
  return tokens.some((tok) => h.includes(tok));
}

// ─────────────────────────────────────────────────────────────────────
// Header-row detection + delimiter sniff
// ─────────────────────────────────────────────────────────────────────

// Mirrors _is_date_header_line — looks for a date-y token AND a delimiter
// on the same line. Used to skip preamble rows ("Account: X", "Statement
// period…") before the real column header.
export function isDateHeaderLine(line: string): boolean {
  const low = (line || "").toLowerCase();
  if (!DATE_HEADER_TOKENS.some((tok) => low.includes(tok))) return false;
  const commaCount = (line.match(/,/g) || []).length;
  const semiCount = (line.match(/;/g) || []).length;
  const pipeCount = (line.match(/\|/g) || []).length;
  return commaCount >= 1 || semiCount >= 1 || line.includes("\t") || pipeCount >= 1;
}

// Sniff the delimiter by counting candidates in the first 20 lines.
// Python uses csv.Sniffer; we do a simpler frequency count. Good enough
// for tabular files — the only ambiguity is comma-vs-semicolon, and
// whichever appears more in the header region wins.
function sniffDelimiter(text: string): string {
  const sample = text.split(/\r?\n/).slice(0, 20).join("\n");
  const counts: Record<string, number> = {
    ",": (sample.match(/,/g) || []).length,
    ";": (sample.match(/;/g) || []).length,
    "\t": (sample.match(/\t/g) || []).length,
    "|": (sample.match(/\|/g) || []).length,
  };
  let best = ",";
  let bestCount = counts[","];
  for (const [d, n] of Object.entries(counts)) {
    if (n > bestCount) {
      best = d;
      bestCount = n;
    }
  }
  return best;
}

// ─────────────────────────────────────────────────────────────────────
// Column picker — port of _pick_generic_columns
// ─────────────────────────────────────────────────────────────────────

interface ColumnMapping {
  date_col: string;
  amount_col?: string;
  debit_col?: string;
  credit_col?: string;
  status_col?: string;
  account_col?: string;
  desc_cols?: string[];
}

function pickGenericColumns(
  headers: string[],
  rows: Array<Record<string, string>>,
): ColumnMapping | null {
  if (headers.length === 0 || rows.length === 0) return null;
  const sample = rows.slice(0, 25);

  function score(col: string, predicate: (v: string) => boolean): number {
    const nonBlank = sample
      .map((r) => r[col] ?? "")
      .filter((v) => v.trim().length > 0);
    if (nonBlank.length === 0) return 0;
    let hits = 0;
    for (const v of nonBlank) if (predicate(v)) hits++;
    return hits / nonBlank.length;
  }

  const dateScores: Record<string, number> = {};
  const amountScores: Record<string, number> = {};
  for (const h of headers) {
    dateScores[h] = score(h, looksLikeDate);
    amountScores[h] = score(h, looksLikeAmount);
  }

  let dateCol = headers.reduce((best, h) =>
    dateScores[h] > dateScores[best] ? h : best,
  headers[0]);
  if (dateScores[dateCol] < 0.7) return null;

  // Header-name preference: prefer an explicit "Date / Posted / ..."
  // column over a random higher-scoring one (e.g. "Account opened").
  const namedDate = headers.find(
    (c) => headerMatchesAny(c, DATE_HEADER_TOKENS) && (dateScores[c] ?? 0) >= 0.7,
  );
  if (namedDate) dateCol = namedDate;

  const moneyCols = headers.filter((c) => {
    if (c === dateCol) return false;
    if ((amountScores[c] ?? 0) < 0.7) return false;
    return sample.some((r) => {
      const v = r[c] ?? "";
      return /[.\-(),$€£]/.test(v);
    });
  });
  if (moneyCols.length === 0) return null;

  const result: ColumnMapping = { date_col: dateCol };

  const debitCol = moneyCols.find((c) => headerMatchesAny(c, DEBIT_HEADER_TOKENS)) ?? null;
  const creditCol = moneyCols.find((c) => headerMatchesAny(c, CREDIT_HEADER_TOKENS)) ?? null;
  if (debitCol && creditCol && debitCol !== creditCol) {
    const exclusive = sample.filter((r) => {
      const dv = (r[debitCol] ?? "").trim();
      const cv = (r[creditCol] ?? "").trim();
      return Boolean(dv) !== Boolean(cv);
    }).length;
    if (exclusive >= Math.max(1, Math.floor(sample.length / 2))) {
      result.debit_col = debitCol;
      result.credit_col = creditCol;
    }
  }

  if (!result.debit_col) {
    // Single signed-amount column. Prefer one with explicit semantic
    // hints in the name. "amount" / "value" are bank-statement standards;
    // "total" / "net" / "gross" are e-commerce-export standards
    // (Shopify, Stripe-like). When several candidates match, pick the
    // first-listed (which gives e-commerce exports `Total` ordering over
    // `Subtotal` because Total appears later in the row but ranks higher
    // here via the explicit ordering below).
    const preferenceOrder = ["amount", "value", "transaction", "total", "net amount", "net", "gross"];
    // Word-boundary match so "Total" is preferred over "Subtotal" — pure
    // substring catches "Subtotal" first when iterating Shopify's
    // `Subtotal,Taxes,Shipping,Total` header order.
    const matchesToken = (col: string, tok: string) => {
      const lc = col.toLowerCase().trim();
      return lc === tok || lc.startsWith(tok + " ") || lc.endsWith(" " + tok) || lc.includes(" " + tok + " ");
    };
    let preferred: string | undefined;
    for (const tok of preferenceOrder) {
      preferred = moneyCols.find((c) => matchesToken(c, tok));
      if (preferred) break;
    }
    // Final fallback: substring match (catches "Total Charged", "Net Amount Earned", etc.).
    if (!preferred) {
      for (const tok of preferenceOrder) {
        preferred = moneyCols.find((c) => c.toLowerCase().includes(tok));
        if (preferred) break;
      }
    }
    result.amount_col = preferred ?? moneyCols.reduce((best, c) => {
      const sa = amountScores[c] ?? 0;
      const sb = amountScores[best] ?? 0;
      const nonBlankA = sample.filter((r) => (r[c] ?? "").trim()).length;
      const nonBlankB = sample.filter((r) => (r[best] ?? "").trim()).length;
      // Tie-breaker: more non-blank cells wins.
      if (sa > sb) return c;
      if (sa < sb) return best;
      return nonBlankA > nonBlankB ? c : best;
    }, moneyCols[0]);
  }

  const claimed = new Set<string>([
    result.date_col,
    result.amount_col,
    result.debit_col,
    result.credit_col,
  ].filter(Boolean) as string[]);

  const statusCol = headers.find(
    (c) => !claimed.has(c) && headerMatchesAny(c, STATUS_HEADER_TOKENS),
  );
  if (statusCol) {
    result.status_col = statusCol;
    claimed.add(statusCol);
  }

  const accountCol = headers.find(
    (c) => !claimed.has(c) && headerMatchesAny(c, ACCOUNT_HEADER_TOKENS),
  );
  if (accountCol) {
    result.account_col = accountCol;
    claimed.add(accountCol);
  }

  let descCols = headers.filter(
    (c) => !claimed.has(c) && headerMatchesAny(c, DESC_HEADER_TOKENS),
  );
  if (descCols.length === 0) {
    // No named description column — pick the most alpha-dense remaining
    // column as the fallback. That's usually the merchant/memo text.
    const candidates = headers.filter((c) => !claimed.has(c));
    if (candidates.length > 0) {
      const alphaDensity = (c: string): number => {
        const vals = sample.map((r) => r[c] ?? "");
        if (vals.length === 0) return 0;
        let alpha = 0;
        let total = 0;
        for (const v of vals) {
          total += v.length;
          for (const ch of v) if (/[A-Za-z]/.test(ch)) alpha++;
        }
        return total === 0 ? 0 : alpha / total;
      };
      const best = candidates.reduce((b, c) => {
        const da = alphaDensity(c);
        const db = alphaDensity(b);
        if (da > db) return c;
        if (da < db) return b;
        const la = sample.reduce((n, r) => n + (r[c] ?? "").length, 0);
        const lb = sample.reduce((n, r) => n + (r[b] ?? "").length, 0);
        return la > lb ? c : b;
      }, candidates[0]);
      descCols = [best];
    }
  }
  if (descCols.length > 0) result.desc_cols = descCols;

  return result;
}

// ─────────────────────────────────────────────────────────────────────
// Institution detection — simplified port of _detect_generic_institution.
// Smaller table than the Python version (top ~25 banks); the user can
// always rename "generic" in the UI if we miss their bank.
// ─────────────────────────────────────────────────────────────────────

// Bank tokens — mirrors scripts/loaders.py::_GENERIC_BANK_TOKENS at
// 2026-05-15 sync. Order matters: more specific tokens FIRST so they
// take priority. Patterns are \b-anchored so vendor descriptions like
// "Robinhood Markets" don't accidentally tag the WHOLE file as Robinhood.
const BANK_TOKENS: Array<[RegExp, string]> = [
  // Canadian banks (primary user base)
  [/\btd canada trust\b/, "TD Canada Trust"],
  [/\btoronto[- ]dominion\b/, "TD"],
  [/\btd bank\b/, "TD"],
  [/\bbank of montreal\b/, "BMO"],
  [/\bbmo\b/, "BMO"],
  [/\broyal bank of canada\b/, "RBC"],
  [/\brbc royal bank\b/, "RBC"],
  [/\bcanadian imperial bank\b/, "CIBC"],
  [/\bcibc\b/, "CIBC"],
  [/\bbank of nova scotia\b/, "Scotiabank"],
  [/\bscotiabank\b/, "Scotiabank"],
  [/\bnational bank of canada\b/, "National Bank"],
  [/\bbanque nationale\b/, "National Bank"],
  [/\btangerine\b/, "Tangerine"],
  [/\bsimplii\b/, "Simplii"],
  [/\beq bank\b/, "EQ Bank"],
  [/\bkoho\b/, "KOHO"],
  [/\bneo financial\b/, "Neo Financial"],
  [/\bmanulife bank\b/, "Manulife"],
  [/\bdesjardins\b/, "Desjardins"],
  [/\batb financial\b/, "ATB"],
  [/\bmeridian credit union\b/, "Meridian"],
  [/\bpc financial\b/, "PC Financial"],
  [/\bpresident'?s choice\b/, "PC Financial"],
  [/\bcanadian tire bank\b/, "Canadian Tire Bank"],
  [/\blaurentian bank\b/, "Laurentian"],
  [/\bcoast capital\b/, "Coast Capital"],
  // US + global majors
  [/\bhsbc\b/, "HSBC"],
  [/\bjpmorgan chase\b/, "Chase"],
  [/\bchase\b/, "Chase"],
  [/\bbank of america\b/, "Bank of America"],
  [/\bwells fargo\b/, "Wells Fargo"],
  [/\bcitibank\b/, "Citi"],
  [/\bcitigroup\b/, "Citi"],
  [/\bcapital one\b/, "Capital One"],
  [/\bdiscover bank\b/, "Discover"],
  [/\bdiscover\b/, "Discover"],
  [/\bu\.?s\.? bank\b/, "U.S. Bank"],
  [/\bpnc bank\b/, "PNC"],
  [/\bfifth third\b/, "Fifth Third"],
  [/\bamerican express\b/, "American Express"],
  [/\bally bank\b/, "Ally"],
  [/\busaa\b/, "USAA"],
  [/\bgoldman sachs\b/, "Marcus"],
  // Fintech / wallets
  [/\bpaypal\b/, "PayPal"],
  [/\btransferwise\b/, "Wise"],
  [/\bwise payments\b/, "Wise"],
  [/\bwise\b/, "Wise"],
  [/\bvenmo\b/, "Venmo"],
  [/\brevolut\b/, "Revolut"],
  [/\bmonzo\b/, "Monzo"],
  [/\bn26\b/, "N26"],
  [/\bchime\b/, "Chime"],
  [/\bwealthsimple\b/, "Wealthsimple"],
  [/\brobinhood\b/, "Robinhood"],
  [/\bcash app\b/, "Cash App"],
  [/\bstripe\b/, "Stripe"],
];

// Account-kind keywords (longer tokens FIRST). NO bare \bdebit\b /
// \bcredit\b — those are tx Type column values in nearly every export
// and would false-positive on every file.
const ACCOUNT_KIND_TOKENS: Array<[RegExp, string]> = [
  [/\bline of credit\b/, "loc"],
  [/\bmoney market\b/, "money-market"],
  [/\bcredit card\b/, "credit"],
  [/\bchequing\b/, "chequing"],
  [/\bchecking\b/, "checking"],
  [/\bsavings\b/, "savings"],
  [/\bmastercard\b/, "mastercard"],
  [/\bvisa\b/, "visa"],
  [/\bamex\b/, "amex"],
  [/\bmortgage\b/, "mortgage"],
  [/\binvestment\b/, "investment"],
  [/\bbrokerage\b/, "brokerage"],
  [/\btfsa\b/, "tfsa"],
  [/\brrsp\b/, "rrsp"],
];

// Pretty-print an account kind for the account label ("chequing" →
// "Chequing", "credit" → "Credit Card"). Keeps the "BMO Chequing"
// composed label readable.
const ACCOUNT_KIND_DISPLAY: Record<string, string> = {
  chequing: "Chequing",
  checking: "Checking",
  savings: "Savings",
  credit: "Credit Card",
  mastercard: "Mastercard",
  visa: "Visa",
  amex: "AmEx",
  "money-market": "Money Market",
  loc: "Line of Credit",
  mortgage: "Mortgage",
  investment: "Investment",
  brokerage: "Brokerage",
  tfsa: "TFSA",
  rrsp: "RRSP",
};

/** Compose a user-facing account label from detected institution +
 *  account kind + filename fallback. Examples:
 *    institution="BMO", kind="chequing"  → "BMO Chequing"
 *    institution="BMO", kind=""          → "BMO"
 *    institution="",    kind="checking"  → "checking"
 *    institution="",    kind=""          → filename stem ("td_export_2026")
 */
function composeAccountLabel(
  institution: string,
  kind: string,
  filenameStem: string,
): string {
  if (institution && kind) {
    return `${institution} ${ACCOUNT_KIND_DISPLAY[kind] ?? kind}`;
  }
  if (institution) return institution;
  if (kind) return kind;
  return filenameStem || "generic";
}

function detectGenericInstitution(
  text: string,
  filename: string,
): { institution: string; kind: string } {
  // Only scan the preamble + header line — body rows can contain bank
  // names in vendor descriptions and cause false positives.
  const dateRow = /^\s*(\d{1,4}[-/]\d{1,2}[-/]\d{1,4}|\d{8})[\s,;\t|"']/;
  const headLines: string[] = [];
  for (const ln of text.split(/\r?\n/).slice(0, 20)) {
    if (dateRow.test(ln)) break;
    headLines.push(ln);
  }
  // Underscores aren't word boundaries for `\b`, so "hsbc_apr.csv" would
  // miss `\bhsbc\b`. Normalize `_` and `-` to spaces in the filename.
  const fname = (filename || "").toLowerCase().replace(/[_-]/g, " ");
  const sample = headLines.join("\n").toLowerCase() + "\n" + fname;
  let institution = "";
  for (const [pat, label] of BANK_TOKENS) {
    if (pat.test(sample)) {
      institution = label;
      break;
    }
  }
  let kind = "";
  for (const [pat, label] of ACCOUNT_KIND_TOKENS) {
    if (pat.test(sample)) {
      kind = label;
      break;
    }
  }
  return { institution, kind };
}

// ─────────────────────────────────────────────────────────────────────
// Public entry point — mirrors load_generic_csv
// ─────────────────────────────────────────────────────────────────────

export async function loadGenericCsv(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  if (!text.trim()) return [];

  // Find the header row. Skip up to 15 preamble lines until we hit one
  // that looks like a date-y header.
  const rawLines = text.split(/\r?\n/);
  let headerIdx = 0;
  for (let i = 0; i < Math.min(15, rawLines.length); i++) {
    if (isDateHeaderLine(rawLines[i])) {
      headerIdx = i;
      break;
    }
  }
  const body = rawLines.slice(headerIdx).join("\n");

  // Use our CSV parser. parseCsv only handles `,` as a separator — if the
  // sniffed delimiter is something else, swap it for a comma first. Quoted
  // fields are rare in the alternative-delimiter case, so this is safe.
  const delim = sniffDelimiter(body);
  const normalized = delim === "," ? body : body.replace(new RegExp(delim.replace(/[\\^$.*+?()[\]{}|]/g, "\\$&"), "g"), ",");

  const parsed = parseCsv(normalized);
  if (parsed.length < 2) return [];
  const headers = parsed[0].map((h) => (h || "").trim());
  if (headers.length === 0 || headers.every((h) => !h)) return [];
  const rows = toDictRows(headers, parsed.slice(1));
  if (rows.length === 0) return [];

  const mapping = pickGenericColumns(headers, rows);
  if (!mapping || !mapping.date_col) return [];
  if (!mapping.amount_col && !mapping.debit_col) return [];

  const { institution, kind: institutionAccountKind } = detectGenericInstitution(text, filename);
  const stemFallback =
    filename.replace(/\.[^.]+$/, "").toLowerCase().replace(/\s+/g, "_").slice(0, 40) || "generic";
  // Compose a friendly label: "BMO Chequing" beats "chequing" or
  // "bmo_chequing_2026". When we have neither institution nor kind,
  // fall back to the filename stem so the label is still meaningful.
  const defaultAccount = composeAccountLabel(institution, institutionAccountKind, stemFallback);

  // Multi-signal sign detection. Combines header markers, institution
  // detection, filename hint, transaction-description patterns, and
  // sign distribution into a weighted score. See decideSignFromSignals
  // above for the contract. The decision lands on each Transaction
  // via raw.sign_decision so misfires are debuggable from the UI.
  const signDecision = decideSignFromSignals({
    headers,
    rows,
    amountCol: mapping.amount_col,
    hasDebitCreditSplit: !!(mapping.debit_col && mapping.credit_col),
    institutionKind: institutionAccountKind,
    filenameLower: filename.toLowerCase(),
  });
  const accountKind: AccountKind = signDecision.account_kind;
  const flipNegatives = signDecision.flip_negatives;

  const out: Transaction[] = [];
  for (const row of rows) {
    if (mapping.status_col) {
      const statusVal = (row[mapping.status_col] || "").trim().toLowerCase();
      if (DROP_STATUS_VALUES.has(statusVal)) continue;
    }

    let dateRaw = (row[mapping.date_col] || "").trim();
    if (!dateRaw) continue;
    // ISO 8601 with time component — chop the time off before parseDate.
    const isoM = /^(\d{4}-\d{1,2}-\d{1,2})[T ]/.exec(dateRaw);
    if (isoM) dateRaw = isoM[1];
    let dateStr: string;
    try {
      dateStr = parseDate(dateRaw);
    } catch {
      continue;
    }

    let amount = 0;
    if (mapping.debit_col && mapping.credit_col) {
      const debitRaw = (row[mapping.debit_col] || "").trim();
      const creditRaw = (row[mapping.credit_col] || "").trim();
      const debit = debitRaw ? parseAmount(debitRaw) : 0;
      const credit = creditRaw ? parseAmount(creditRaw) : 0;
      amount = Math.abs(debit) - Math.abs(credit);
    } else if (mapping.amount_col) {
      const rawAmt = (row[mapping.amount_col] || "").trim();
      if (!rawAmt) continue;
      const signed = parseAmount(rawAmt);
      // Sign decision came from the dominant-sign pre-scan above (with
      // account_kind as a tiebreaker). flipNegatives=true → bank ships
      // negative as expense, we flip. flipNegatives=false → bank already
      // ships positive as expense, pass through.
      amount = flipNegatives ? -signed : signed;
    } else {
      continue;
    }

    let description = "";
    if (mapping.desc_cols && mapping.desc_cols.length > 0) {
      const parts: string[] = [];
      const seen = new Set<string>();
      for (const c of mapping.desc_cols) {
        const v = (row[c] || "").trim();
        const low = v.toLowerCase();
        if (v && !seen.has(low)) {
          parts.push(v);
          seen.add(low);
        }
      }
      description = parts.join(" - ");
    }
    if (!description) {
      const claimed = new Set<string>([
        mapping.date_col,
        mapping.amount_col,
        mapping.debit_col,
        mapping.credit_col,
        mapping.status_col,
        mapping.account_col,
        ...(mapping.desc_cols ?? []),
      ].filter(Boolean) as string[]);
      description = headers
        .filter((c) => !claimed.has(c) && (row[c] || "").trim().length > 0)
        .map((c) => (row[c] || "").trim())
        .join(" ")
        .slice(0, 120);
    }
    if (!description) description = "Unknown transaction";

    if (isNonTxRow(description)) continue;
    if (isInterPortalTransfer(description)) continue;

    const id = await makeId("generic", defaultAccount, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "unknown",
        amount: Math.round(amount * 100) / 100,
        portal: "generic",
        account: defaultAccount,
        account_kind: accountKind,
        institution_name: institution,
        source_file: filename,
        raw: {
          _generic_mapping: mapping as unknown as Record<string, unknown>,
          original_row: row,
          sign_decision: signDecision as unknown as Record<string, unknown>,
        },
      }),
    );
  }
  return out;
}

// Exported for tests + the dispatcher's "should I bother with the generic
// fallback?" probe.
export const _internal = {
  looksLikeDate,
  looksLikeAmount,
  headerMatchesAny,
  pickGenericColumns,
  detectGenericInstitution,
  isNonTxRow,
  composeAccountLabel,
  decideSignFromSignals,
};
